import classNames from "classnames";
import {
	createContext,
	PropsWithChildren,
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	KeyboardEvent
} from "react";
import {
	CollapsibleMenu,
	DropDownDirection,
} from "../CollapsibleMenu/CollapsibleMenu";
import {
	CollapsibleListItemContext,
	HorizontalAlignment
} from "../CollapsibleList/CollapsibleList";
import { ToolbarButton } from "./ToolbarButton";
import { ToolbarSplitButton } from "./ToolbarSplitButton";
import styles from "./Toolbar.module.less";
import { ToolbarCheckbox } from "./ToolbarCheckbox";
import { ToolbarMenuButton } from "./ToolbarMenuButton";
import { ToolbarInlineOptionSelector } from "./ToolbarInlineOptionSelector";
import { ToolbarItem } from "./ToolbarItem";
import { ToolbarCopyButton } from "./ToolbarCopyButton";
import {
	IconButton,
	IconButtonKind,
	IconButtonSize
} from "../IconButton/IconButton";
import { ButtonSize } from "../Button/Button";
import { ToolbarAlignmentContext } from "./ToolbarAlignmentContext";
import { ToolbarToggle } from "./ToolbarToggle";
import { ToolbarDivider } from "./ToolbarDivider";

export type ToolbarProps = {
	dropDownDirection?: DropDownDirection;
	horizontalAlignment?: HorizontalAlignment;
	moreButtonKind?: IconButtonKind;
	hasNoMarginBetweenItems?: boolean;
	buttonSize?: ButtonSize;
	onMaxContentWidthChange?(maxContentWidth: number): void;
	trackingId?: string;
};

export const Toolbar = (props: PropsWithChildren<ToolbarProps>): ReactElement => {
	const {
		horizontalAlignment: horizontalAlignmentFromProps,
		dropDownDirection: dropDownDirectionFromProps,
		moreButtonKind = "secondary",
		hasNoMarginBetweenItems = false,
		buttonSize = "normal",
		onMaxContentWidthChange,
		trackingId,
		children
	} = props;

	const {
		horizontalAlignment: horizontalAlignmentFromContext,
		dropDownDirection: dropDownDirectionFromContext
	} = useContext(ToolbarAlignmentContext);

	const horizontalAlignment: HorizontalAlignment | undefined = horizontalAlignmentFromProps
		?? horizontalAlignmentFromContext;
	const dropDownDirection: DropDownDirection = dropDownDirectionFromProps
		?? dropDownDirectionFromContext
		?? "down";

	const renderMoreButton = useCallback(
		(
			isActive: boolean,
			tabIndex: number,
			onIsActiveChange: (value: boolean) => void,
			onKeyDown: (args: KeyboardEvent<HTMLElement>) => void,
			ref: (element: HTMLElement | null) => void
		): ReactElement => {
			return (
				<div className={styles.moreButtonWrapper}>
					<IconButton
						kind={moreButtonKind}
						icon="ellipsis"
						shape="round"
						onClick={() => onIsActiveChange(!isActive)}
						trackingId="more-icon"
						isActive={isActive}
						size={buttonSizeToIconButtonSizeMap[buttonSize]}
						tabIndex={tabIndex}
						onKeyDown={onKeyDown}
						ref={ref}
					/>
				</div>
			);
		},
		[
			moreButtonKind,
			buttonSize
		]
	);

	const toolbarContextData = useMemo(
		(): ToolbarContextData => {
			return {
				dropDownDirection,
				buttonSize
			};
		},
		[
			dropDownDirection,
			buttonSize
		]
	);

	return (
		<div
			className={classNames(
				styles.root,
				{ [styles.root_noMargin]: hasNoMarginBetweenItems }
			)}
			tabIndex={-1}
		>
			<div className={styles.spaceAligner}>
				<ToolbarContext.Provider value={toolbarContextData}>
					<CollapsibleMenu
						dropDownDirection={dropDownDirection}
						verticalAlignment="center"
						horizontalAlignment={horizontalAlignment}
						moreButtonFactory={renderMoreButton}
						onMaxContentWidthChange={onMaxContentWidthChange}
						trackingId={trackingId}
					>
						{children}
					</CollapsibleMenu>
				</ToolbarContext.Provider>
			</div>
		</div>
	);
};
Toolbar.displayName = "Toolbar";

const ToolbarSpaceBetween = (): null => {
	const {
		onResponsiveBehaviorChange,
		onIsSpaceBetweenChange,
		resetResponsiveBehavior,
		resetIsSpaceBetween
	} = useContext(CollapsibleListItemContext);

	useEffect(
		(): EffectWithDestructorResult => {
			onResponsiveBehaviorChange("always-visible");
			onIsSpaceBetweenChange(true);

			return (): void => {
				resetResponsiveBehavior();
				resetIsSpaceBetween();
			};
		},
		[
			onResponsiveBehaviorChange,
			onIsSpaceBetweenChange,
			resetResponsiveBehavior,
			resetIsSpaceBetween
		]
	);

	return null;
};
ToolbarSpaceBetween.displayName = "Toolbar.SpaceBetween";

Toolbar.Button = ToolbarButton;
Toolbar.SpaceBetween = ToolbarSpaceBetween;
Toolbar.SplitButton = ToolbarSplitButton;
Toolbar.MenuButton = ToolbarMenuButton;
Toolbar.Checkbox = ToolbarCheckbox;
Toolbar.InlineOptionSelector = ToolbarInlineOptionSelector;
Toolbar.CopyButton = ToolbarCopyButton;
Toolbar.Custom = ToolbarItem;
Toolbar.Toggle = ToolbarToggle;
Toolbar.Divider = ToolbarDivider;

export const ToolbarPriority = {
	LowPriorityItem: -1,
	NotDefined: 0,
	Default: 1,
	PrimaryButton: 2
};

export type ToolbarContextData = {
	dropDownDirection: DropDownDirection;
	buttonSize: ButtonSize;
};

export const ToolbarContext = createContext<ToolbarContextData>({
	dropDownDirection: "down",
	buttonSize: "normal"
});

export const buttonSizeToIconButtonSizeMap: {[key in ButtonSize]: IconButtonSize} = {
	"normal": "normal",
	"large": "large"
};

export {
	type DropDownDirection,
	type HorizontalAlignment
};